import {
    afterVersion,
    answerSlot,
    applyMutation,
    entityKeys,
    multiCountSlot,
    newUlid,
    requireTenantDb,
    type AnswerRecord,
    type MultiCountRecord,
    type MutationOperation,
    type TenantDatabase,
    type WireScope,
} from '@/shared/offline';

import { InspectionLockedError, isSubmitted } from './lock';
import { answerFieldsOf, type AnswerFields } from './model';

/**
 * Alle schrijfacties op een opname. Ze schrijven lokaal en zetten de
 * bewerking in de outbox, in exact de vorm van het draadformaat; de UI wacht
 * nooit op het netwerk.
 *
 * Een antwoord hoort bij (vraag, bereik). Het `answer_uuid` wordt één keer op
 * het apparaat gemaakt en blijft daarna hetzelfde, zodat een herhaalde push
 * dezelfde rij bijwerkt.
 */

export interface AnswerTarget {
    inspectionUuid: string;
    questionId: number;
    multiScope: WireScope;
}

export interface SaveAnswerInput extends AnswerTarget {
    fields: Partial<AnswerFields>;
}

/**
 * Een wijziging die voortbouwt op wat er op dit moment lokaal staat: de velden
 * van het bewaarde antwoord (of `undefined`) in, de nieuwe velden uit - of
 * `null` om het antwoord te wissen. Puur en synchroon: hij draait binnen de
 * transactie.
 */
export type AnswerChange = (current: AnswerFields | undefined) => Partial<AnswerFields> | null;

/** Wat er van één opname lokaal staat, voor een wijziging die daarop moet voortbouwen. */
export interface LocalAnswers {
    answers: readonly AnswerRecord[];
    multiCounts: readonly MultiCountRecord[];
}

/**
 * Een opname die nog niet gestart is, start bij de eerste wijziging: status
 * `in_progress` en `started_at` gaan in dezelfde transactie mee.
 */
async function startIfNeeded(
    db: TenantDatabase,
    inspectionUuid: string,
    clientUpdatedAt: string,
): Promise<MutationOperation[]> {
    const inspection = await db.inspections.get(inspectionUuid);

    if (isSubmitted(inspection)) {
        throw new InspectionLockedError(inspectionUuid);
    }

    if (!inspection || inspection.status !== 'not_started') {
        return [];
    }

    const startedAt = inspection.startedAt ?? clientUpdatedAt;

    await db.inspections.update(inspectionUuid, {
        status: 'in_progress',
        startedAt,
        clientUpdatedAt,
    });

    return [
        {
            body: {
                entity: 'inspection',
                action: 'upsert',
                payload: {
                    inspection_uuid: inspectionUuid,
                    status: 'in_progress',
                    started_at: startedAt,
                    completed_at: null,
                    notes: inspection.notes,
                },
            },
            inspectionUuid,
            entityKey: entityKeys.inspection(inspectionUuid),
        },
    ];
}

async function writeAnswer(
    db: TenantDatabase,
    input: SaveAnswerInput,
    clientUpdatedAt: string,
): Promise<MutationOperation> {
    const slot = answerSlot(input.questionId, input.multiScope);
    const existing = await db.answers
        .where('[inspectionUuid+slot]')
        .equals([input.inspectionUuid, slot])
        .first();
    const fields: AnswerFields = { ...answerFieldsOf(existing), ...input.fields };
    const at = afterVersion(clientUpdatedAt, existing?.clientUpdatedAt);
    const record: AnswerRecord = {
        answerUuid: existing?.answerUuid ?? newUlid(),
        inspectionUuid: input.inspectionUuid,
        questionId: input.questionId,
        multiScope: [...input.multiScope],
        slot,
        ...fields,
        clientUpdatedAt: at,
        confirmed: existing?.confirmed ?? 0,
    };

    await db.answers.put(record);

    return {
        body: {
            entity: 'inspection_answer',
            action: 'upsert',
            payload: {
                inspection_uuid: record.inspectionUuid,
                answer_uuid: record.answerUuid,
                question_id: record.questionId,
                multi_scope: [...record.multiScope],
                option_ids: fields.optionIds,
                value_number: fields.valueNumber,
                value_text: fields.valueText,
                report_text: fields.reportText,
                location: fields.location,
                direct_cost: fields.directCost,
                term_cost: fields.termCost,
                improvement_cost: fields.improvementCost,
                cost_group_id: fields.costGroupId,
                urgency: fields.urgency,
                nhg_classification: fields.nhgClassification,
            },
        },
        inspectionUuid: record.inspectionUuid,
        entityKey: entityKeys.answer(record.inspectionUuid, slot),
        clientUpdatedAt: at,
    };
}

async function answerAt(
    db: TenantDatabase,
    target: AnswerTarget,
): Promise<AnswerRecord | undefined> {
    return db.answers
        .where('[inspectionUuid+slot]')
        .equals([target.inspectionUuid, answerSlot(target.questionId, target.multiScope)])
        .first();
}

/**
 * Wist een antwoord: lokaal verdwijnt de rij, over de lijn gaat een `delete`.
 * Heeft de server het antwoord nooit gezien, dan heft de `delete` een nog niet
 * verstuurde upsert op.
 */
async function removeAnswer(
    db: TenantDatabase,
    target: AnswerTarget,
    clientUpdatedAt: string,
): Promise<MutationOperation[]> {
    const existing = await answerAt(db, target);

    if (!existing) {
        return [];
    }

    const slot = answerSlot(target.questionId, target.multiScope);

    await db.answers.delete(existing.answerUuid);

    return [
        {
            body: {
                entity: 'inspection_answer',
                action: 'delete',
                payload: {
                    inspection_uuid: target.inspectionUuid,
                    answer_uuid: existing.answerUuid,
                },
            },
            inspectionUuid: target.inspectionUuid,
            entityKey: entityKeys.answer(target.inspectionUuid, slot),
            unsynced: existing.confirmed === 0,
            clientUpdatedAt: afterVersion(clientUpdatedAt, existing.clientUpdatedAt),
        },
    ];
}

function sameFields(a: AnswerFields, b: AnswerFields): boolean {
    return (Object.keys(a) as Array<keyof AnswerFields>).every(
        (field) => JSON.stringify(a[field]) === JSON.stringify(b[field]),
    );
}

export const inspectionMutations = {
    /** Start de opname ("Starten" op Mijn dag). Doet niets als hij al loopt. */
    async start(inspectionUuid: string, db: TenantDatabase = requireTenantDb()) {
        return applyMutation(
            {
                apply: (database, clientUpdatedAt) =>
                    startIfNeeded(database, inspectionUuid, clientUpdatedAt),
            },
            db,
        );
    },

    /** Zet (een deel van) één antwoord; de overige velden blijven staan. */
    async saveAnswer(input: SaveAnswerInput, db: TenantDatabase = requireTenantDb()) {
        return applyMutation(
            {
                apply: async (database, clientUpdatedAt) => [
                    ...(await startIfNeeded(database, input.inspectionUuid, clientUpdatedAt)),
                    await writeAnswer(database, input, clientUpdatedAt),
                ],
            },
            db,
        );
    },

    /**
     * Wist een antwoord. Lokaal verdwijnt de rij; over de lijn gaat een
     * `delete`. Heeft de server het antwoord nooit gezien en is de upsert nog
     * niet verstuurd, dan heffen die twee elkaar op.
     */
    async clearAnswer(target: AnswerTarget, db: TenantDatabase = requireTenantDb()) {
        return applyMutation(
            {
                apply: async (database, clientUpdatedAt) => {
                    if (isSubmitted(await database.inspections.get(target.inspectionUuid))) {
                        throw new InspectionLockedError(target.inspectionUuid);
                    }

                    return removeAnswer(database, target, clientUpdatedAt);
                },
            },
            db,
        );
    },

    /**
     * Wijzigt één antwoord op basis van wat er *nu* lokaal staat, niet op basis
     * van wat het scherm liet zien. Een tweede tik die sneller komt dan het
     * scherm, bouwt zo voort op de eerste in plaats van hem terug te draaien.
     * Verandert er niets, dan gaat er ook niets over de lijn.
     */
    async updateAnswer(
        target: AnswerTarget,
        change: AnswerChange,
        db: TenantDatabase = requireTenantDb(),
    ) {
        return applyMutation(
            {
                apply: async (database, clientUpdatedAt) => {
                    if (isSubmitted(await database.inspections.get(target.inspectionUuid))) {
                        throw new InspectionLockedError(target.inspectionUuid);
                    }

                    const existing = await answerAt(database, target);
                    const current = existing ? answerFieldsOf(existing) : undefined;
                    const next = change(current);

                    if (next === null) {
                        return removeAnswer(database, target, clientUpdatedAt);
                    }

                    if (current && sameFields({ ...current, ...next }, current)) {
                        return [];
                    }

                    return [
                        ...(await startIfNeeded(database, target.inspectionUuid, clientUpdatedAt)),
                        await writeAnswer(database, { ...target, fields: next }, clientUpdatedAt),
                    ];
                },
            },
            db,
        );
    },

    /**
     * Meerdere antwoorden in één transactie, bepaald door wat er op dat moment
     * lokaal van de opname staat - zoals "Geen gebreken hier": alleen wat dán nog
     * open is, wordt ingevuld. Een antwoord dat net gegeven werd maar nog niet op
     * het scherm stond, blijft zo staan.
     */
    async saveAnswersFrom(
        inspectionUuid: string,
        plan: (current: LocalAnswers) => readonly SaveAnswerInput[],
        db: TenantDatabase = requireTenantDb(),
    ) {
        return applyMutation(
            {
                apply: async (database, clientUpdatedAt) => {
                    const answers = await database.answers
                        .where('inspectionUuid')
                        .equals(inspectionUuid)
                        .toArray();
                    const multiCounts = await database.multiCounts
                        .where('inspectionUuid')
                        .equals(inspectionUuid)
                        .toArray();
                    const inputs = plan({ answers, multiCounts });

                    if (inputs.length === 0) {
                        return [];
                    }

                    const operations = await startIfNeeded(
                        database,
                        inspectionUuid,
                        clientUpdatedAt,
                    );

                    for (const input of inputs) {
                        operations.push(await writeAnswer(database, input, clientUpdatedAt));
                    }

                    return operations;
                },
            },
            db,
        );
    },

    /** "Hoeveel balkons zijn er?" - het aantal instanties van een multi-container. */
    async setMultiCount(
        input: {
            inspectionUuid: string;
            containerNodeId: number;
            multiScope: WireScope;
            count: number;
        },
        db: TenantDatabase = requireTenantDb(),
    ) {
        const count = Math.max(0, Math.trunc(input.count));
        const slot = multiCountSlot(input.containerNodeId, input.multiScope);

        return applyMutation(
            {
                apply: async (database, clientUpdatedAt) => {
                    const operations = await startIfNeeded(
                        database,
                        input.inspectionUuid,
                        clientUpdatedAt,
                    );

                    const existing = await database.multiCounts.get([input.inspectionUuid, slot]);
                    const at = afterVersion(clientUpdatedAt, existing?.clientUpdatedAt);

                    await database.multiCounts.put({
                        inspectionUuid: input.inspectionUuid,
                        slot,
                        containerNodeId: input.containerNodeId,
                        multiScope: [...input.multiScope],
                        count,
                        clientUpdatedAt: at,
                    });

                    operations.push({
                        body: {
                            entity: 'multi_count',
                            action: 'upsert',
                            payload: {
                                inspection_uuid: input.inspectionUuid,
                                container_node_id: input.containerNodeId,
                                multi_scope: [...input.multiScope],
                                count,
                            },
                        },
                        inspectionUuid: input.inspectionUuid,
                        entityKey: entityKeys.multiCount(input.inspectionUuid, slot),
                        clientUpdatedAt: at,
                    });

                    return operations;
                },
            },
            db,
        );
    },

    /**
     * Rondt de opname af: status `completed` en `completed_at`, ook offline.
     * Afronden blokkeert nooit; wat nog open staat, meldt het afrondscherm en
     * het rapport. Na afronden kan de inspecteur nog antwoorden aanpassen, tot
     * hij indient.
     */
    async complete(inspectionUuid: string, db: TenantDatabase = requireTenantDb()) {
        return applyMutation(
            {
                apply: async (database, clientUpdatedAt) => {
                    const inspection = await database.inspections.get(inspectionUuid);

                    if (!inspection) {
                        return [];
                    }

                    if (isSubmitted(inspection)) {
                        throw new InspectionLockedError(inspectionUuid);
                    }

                    const startedAt = inspection.startedAt ?? clientUpdatedAt;
                    const operations = await startIfNeeded(
                        database,
                        inspectionUuid,
                        clientUpdatedAt,
                    );

                    await database.inspections.update(inspectionUuid, {
                        status: 'completed',
                        startedAt,
                        completedAt: clientUpdatedAt,
                        clientUpdatedAt,
                    });

                    operations.push({
                        body: {
                            entity: 'inspection',
                            action: 'upsert',
                            payload: {
                                inspection_uuid: inspectionUuid,
                                status: 'completed',
                                started_at: startedAt,
                                completed_at: clientUpdatedAt,
                                notes: inspection.notes,
                            },
                        },
                        inspectionUuid,
                        entityKey: entityKeys.inspection(inspectionUuid),
                    });

                    return operations;
                },
            },
            db,
        );
    },

    /**
     * De server heeft de opname aangenomen (`POST /submit`). Alleen lokaal: de
     * server weet het al, dus er gaat niets over de lijn.
     */
    async markSubmitted(
        inspectionUuid: string,
        input: { completedAt: string | null; submittedAt: string | null },
        db: TenantDatabase = requireTenantDb(),
    ) {
        return applyMutation(
            {
                apply: async (database, clientUpdatedAt) => {
                    const inspection = await database.inspections.get(inspectionUuid);

                    if (!inspection) {
                        return;
                    }

                    await database.inspections.update(inspectionUuid, {
                        status: 'submitted',
                        completedAt: input.completedAt ?? inspection.completedAt ?? clientUpdatedAt,
                        submittedAt: input.submittedAt ?? clientUpdatedAt,
                    });
                },
            },
            db,
        );
    },

    /**
     * Een lokaal concept zonder draadvorm (het NTA 8800-formulier). Het blijft
     * op het apparaat tot het contract er een entiteit voor heeft.
     */
    async saveDraft(
        inspectionUuid: string,
        name: string,
        value: unknown,
        db: TenantDatabase = requireTenantDb(),
    ) {
        return inspectionMutations.updateDraft(inspectionUuid, name, () => value, db);
    },

    /**
     * Wijzigt een lokaal concept op basis van wat er op het moment van schrijven
     * staat, in dezelfde transactie: twee velden die vlak na elkaar worden
     * opgeslagen, overschrijven elkaar dan niet.
     */
    async updateDraft(
        inspectionUuid: string,
        name: string,
        change: (current: unknown) => unknown,
        db: TenantDatabase = requireTenantDb(),
    ) {
        return applyMutation(
            {
                apply: async (database) => {
                    const inspection = await database.inspections.get(inspectionUuid);

                    if (isSubmitted(inspection)) {
                        throw new InspectionLockedError(inspectionUuid);
                    }

                    if (inspection) {
                        await database.inspections.update(inspectionUuid, {
                            drafts: {
                                ...inspection.drafts,
                                [name]: change(inspection.drafts[name]),
                            },
                        });
                    }
                },
            },
            db,
        );
    },
};
